import { PayloadAction, createSlice, isAnyOf } from "@reduxjs/toolkit";

import { AdminAxios } from "../../axios/admin/admin.axios";
import { AdminMessage } from "../../model/admin/AdminMessage";
import { AdminStats } from "../../model/admin/AdminStats";
import { AdminGetUserMessagesDto } from "../../model/admin/dto/AdminGetUserMessagesDto";
import { AdminGetUserMessagesResponseDto } from "../../model/admin/dto/AdminGetUserMessagesResponseDto";
import { AdminMessageUserDto } from "../../model/admin/dto/AdminMessageUserDto";
import { GetAdminStatsDto } from "../../model/admin/dto/GetAdminStatsDto";
import { SearchUsersDto } from "../../model/admin/dto/SearchUsersDto";
import { SearchUsersResponseDto } from "../../model/admin/dto/SearchUsersResponseDto";
import { ToggleUserStatusDto } from "../../model/admin/dto/ToggleUserStatusDto";
import { AdminCreateSupportSessionDto } from "../../model/admin/dto/AdminCreateSupportSessionDto";
import { LoginResponseDto } from "../../model/user/dto/LoginResponseDto";
import { UpdateUserPlanDto } from "../../model/admin/dto/UpdateUserPlanDto";
import { UpdateUserThresholdsDto } from "../../model/admin/dto/UpdateUserThresholdsDto";
import { GenericWithUserId } from "../../model/shared/dto/GenericWithUserId";
import { AtiraThunk } from "../AtiraThunk";
import { userActions } from "../user/user.slice";
import { AdminCreatePSADto } from "../../model/admin/dto/AdminCreatePSA";
import { PSA } from "../../model/admin/AdminPSA";
import { AdminGetPSAsResponseDto } from "../../model/admin/dto/AdminGetPSAsResponseDto";
import { AdminUpdatePSADto } from "../../model/admin/dto/AdminUpdatePSADto";
import { AdminDeletePSADto } from "../../model/admin/dto/AdminDeletePSADto";
import { AdminResendPSADto } from "../../model/admin/dto/AdminResendPSADto";
import { revertAllReduxAction } from "../store-reset";
import { AdminCreateRoadmapItemDto } from "../../model/admin/dto/AdminCreateRoadmapItemDto";
import { AdminDeleteRoadmapItemDto } from "../../model/admin/dto/AdminDeleteRoadmapItemDto";
import { AdminGetRoadmapItemsResponseDto } from "../../model/admin/dto/AdminGetRoadmapItemsResponseDto";
import { AdminUpdateRoadmapItemDto } from "../../model/admin/dto/AdminUpdateRoadmapItemDto";
import { RoadmapItem } from "../../model/roadmap/RoadmapItem";

interface AdminReducer {
  stats: AdminStats;
  searchUsers: SearchUsersResponseDto["data"];
  searchUsersMeta: SearchUsersDto["meta"];
  searchUsersLoading: boolean;

  searchUsersTablePageSize: number;
  searchUsersTablePage: number;

  adminMessages: AdminMessage[];
  adminMessagesLoading: boolean;
  adminMessagesPage: number;
  adminMessagesPageSize: number;

  PSAs: PSA[];
  PSALoading: boolean;

  roadmapItems: RoadmapItem[];
  roadmapItemsLoading: boolean;
}

const initialState = Object.freeze<AdminReducer>({
  stats: {
    users: {
      total: 0,
      lastWeek: 0,
      lastMonth: 0,
    },
    entry: {
      total: 0,
      lastWeek: 0,
      lastMonth: 0,
    },
    form: {
      total: 0,
      lastWeek: 0,
      lastMonth: 0,
    },
    psa: {
      total: 0,
    },
    roadmap: {
      total: 0,
    },
  },
  searchUsers: [],
  searchUsersMeta: {
    page: 0,
    count: 20,
  },
  searchUsersTablePageSize: 20,
  searchUsersTablePage: 1,
  searchUsersLoading: false,
  adminMessages: [],
  adminMessagesLoading: false,
  adminMessagesPage: 1,
  adminMessagesPageSize: 20,

  PSAs: [],
  PSALoading: false,

  roadmapItems: [],
  roadmapItemsLoading: false,
});

const getAdminStats = AtiraThunk<AdminStats, GetAdminStatsDto>(
  "/admin/stats",
  AdminAxios.getAdminStats,
);

const searchUsers = AtiraThunk<SearchUsersResponseDto, SearchUsersDto>(
  "/admin/user/search",
  AdminAxios.searchUsers,
);

const updateUserPlan = AtiraThunk<void, UpdateUserPlanDto>(
  "/user/:id/plan/update",
  AdminAxios.updateUserPlan,
);

const toggleUserStatus = AtiraThunk<void, ToggleUserStatusDto>(
  "/enable/:id/user",
  AdminAxios.toggleUserStatus,
);

const createSupportSession = AtiraThunk<
  LoginResponseDto,
  AdminCreateSupportSessionDto
>("/admin/user/:id/support-session", AdminAxios.createSupportSession);

const updateUserThresholds = AtiraThunk<void, UpdateUserThresholdsDto>(
  "/user/:id/thersholds",
  AdminAxios.updateUserThresholds,
);

const sendMessageToUser = AtiraThunk<void, AdminMessageUserDto>(
  "/admin/user/:id/message",
  AdminAxios.sendMessageToUser,
);

const getAdminMessages = AtiraThunk<
  AdminGetUserMessagesResponseDto,
  AdminGetUserMessagesDto
>("/admin/messages", (dto) => AdminAxios.getAdminMessages(dto));

const markAdminMessagesAsRead = AtiraThunk<void, GenericWithUserId>(
  "/admin/messages/mark-as-read",
  AdminAxios.markAdminMessagesAsRead,
);

const getPSAs = AtiraThunk<AdminGetPSAsResponseDto, GenericWithUserId>(
  "/admin/psa",
  AdminAxios.getPSAs,
);

const createPSA = AtiraThunk<void, AdminCreatePSADto>(
  "/admin/psa/create",
  AdminAxios.createPSA,
);

const updatePSA = AtiraThunk<void, AdminUpdatePSADto>(
  "/admin/psa/update",
  AdminAxios.updatePSA,
);

const markPSAAsRead = AtiraThunk<void, GenericWithUserId>(
  "/admin/psa/mark-as-read",
  AdminAxios.markPSAAsRead,
);

const deletePSA = AtiraThunk<void, AdminDeletePSADto>(
  "/admin/psa/delete",
  AdminAxios.deletePSA,
);

const resendPSA = AtiraThunk<void, AdminResendPSADto>(
  "/admin/psa/resend",
  AdminAxios.resendPSA,
);

const getRoadmapItems = AtiraThunk<
  AdminGetRoadmapItemsResponseDto,
  GenericWithUserId
>("/admin/roadmap", AdminAxios.getRoadmapItems);

const createRoadmapItem = AtiraThunk<void, AdminCreateRoadmapItemDto>(
  "/admin/roadmap/create",
  AdminAxios.createRoadmapItem,
);

const updateRoadmapItem = AtiraThunk<void, AdminUpdateRoadmapItemDto>(
  "/admin/roadmap/update",
  AdminAxios.updateRoadmapItem,
);

const deleteRoadmapItem = AtiraThunk<void, AdminDeleteRoadmapItemDto>(
  "/admin/roadmap/delete",
  AdminAxios.deleteRoadmapItem,
);

const adminSlice = createSlice({
  name: "admin",
  initialState,
  reducers: {
    setCurrentSearchUsersPage: (state, action: PayloadAction<number>) => {
      state.searchUsersTablePage = action.payload;
    },

    setSearchUsersTablePageSize: (state, action) => {
      state.searchUsersTablePageSize = action.payload;
    },

    setSearchUsersTablePage: (state, action) => {
      state.searchUsersTablePage = action.payload;
    },
  },
  extraReducers: ({ addCase, addMatcher }) => {
    addCase(getAdminStats.fulfilled, (state, action) => {
      state.stats = action.payload;
    });

    addCase(searchUsers.pending, (state) => {
      state.searchUsersLoading = true;
    });

    addCase(searchUsers.fulfilled, (state, action) => {
      state.searchUsers = action.payload.data;
      state.searchUsersMeta = action.payload.meta;
      state.searchUsersLoading = false;
    });

    addCase(searchUsers.rejected, (state) => {
      state.searchUsersLoading = false;
    });

    addCase(getAdminMessages.pending, (state) => {
      state.adminMessagesLoading = true;
    });
    addCase(getAdminMessages.fulfilled, (state, action) => {
      state.adminMessages = action.payload.data;
      state.adminMessagesLoading = false;
      state.adminMessagesPageSize = action.payload.meta.count;
      state.adminMessagesPage = action.payload.meta.page;
    });

    addCase(getPSAs.pending, (state) => {
      state.PSALoading = true;
    });
    addCase(getPSAs.fulfilled, (state, action) => {
      state.PSAs = action.payload.results || [];
      state.PSALoading = false;
    });
    addCase(getPSAs.rejected, (state) => {
      state.PSALoading = false;
    });

    addCase(getRoadmapItems.pending, (state) => {
      state.roadmapItemsLoading = true;
    });
    addCase(getRoadmapItems.fulfilled, (state, action) => {
      state.roadmapItems = action.payload.results || [];
      state.roadmapItemsLoading = false;
    });
    addCase(getRoadmapItems.rejected, (state) => {
      state.roadmapItemsLoading = false;
    });

    addMatcher(isAnyOf(userActions.logout, revertAllReduxAction), (state) => {
      return (state = initialState);
    });
  },
});

export const adminActions = {
  ...adminSlice.actions,
  getAdminStats,
  searchUsers,
  updateUserPlan,
  updateUserThresholds,
  sendMessageToUser,
  toggleUserStatus,
  createSupportSession,
  getAdminMessages,
  markAdminMessagesAsRead,
  getPSAs,
  createPSA,
  updatePSA,
  markPSAAsRead,
  deletePSA,
  resendPSA,
  getRoadmapItems,
  createRoadmapItem,
  updateRoadmapItem,
  deleteRoadmapItem,
};

export default adminSlice.reducer;
